import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { MultiSelect, MultiSelectOption } from "@/components/ui/multi-select";
import { Stepper, Step } from "@/components/ui/stepper";
import { DateRange } from "react-day-picker";
import { Sparkles, Store, Users, Calendar, PackagePlus } from "lucide-react";

export default function ComponentDemos() {
  // Combobox states
  const [selectedStore, setSelectedStore] = useState<string>("");
  const [selectedOperator, setSelectedOperator] = useState<string>("");
  const [selectedRetailer, setSelectedRetailer] = useState<string>("");

  // Date range state
  const [campaignDateRange, setCampaignDateRange] = useState<DateRange | undefined>();

  // Multi-select states
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);

  // Stepper state
  const [currentStep, setCurrentStep] = useState(0);

  // Mock data for comboboxes (realistic to your domain)
  const stores: ComboboxOption[] = [
    { value: "1", label: "Tesco Dublin Central" },
    { value: "2", label: "Tesco Cork City" },
    { value: "3", label: "Dunnes Stores Galway" },
    { value: "4", label: "SuperValu Limerick" },
    { value: "5", label: "Lidl Waterford" },
    { value: "6", label: "Aldi Belfast" },
    { value: "7", label: "Centra Dublin South" },
    { value: "8", label: "Spar Drogheda" },
    { value: "9", label: "Tesco Extra Dundalk" },
    { value: "10", label: "Marks & Spencer Dublin" },
  ];

  const operators: ComboboxOption[] = [
    { value: "1", label: "John Murphy - Active" },
    { value: "2", label: "Sarah O'Brien - Active" },
    { value: "3", label: "Michael Walsh - Active" },
    { value: "4", label: "Emma Kelly - Active" },
    { value: "5", label: "David Ryan - Active" },
    { value: "6", label: "Lisa Doyle - Active" },
    { value: "7", label: "James Brennan - Active" },
    { value: "8", label: "Claire McCarthy - Active" },
  ];

  const retailers: ComboboxOption[] = [
    { value: "1", label: "Tesco Ireland" },
    { value: "2", label: "Dunnes Stores" },
    { value: "3", label: "SuperValu" },
    { value: "4", label: "Lidl Ireland" },
    { value: "5", label: "Aldi Ireland" },
  ];

  const formats: MultiSelectOption[] = [
    { value: "1", label: "A4 Poster" },
    { value: "2", label: "A3 Poster" },
    { value: "3", label: "Window Cling" },
    { value: "4", label: "Shelf Talker" },
    { value: "5", label: "Counter Card" },
    { value: "6", label: "Wobblers" },
    { value: "7", label: "Hanging Sign" },
    { value: "8", label: "Floor Decal" },
  ];

  const campaignSteps: Step[] = [
    { title: "Basic Info", description: "Name & details" },
    { title: "Select Stores", description: "Choose locations" },
    { title: "Pick Formats", description: "Media types" },
    { title: "Assign Operators", description: "Team members" },
    { title: "Review", description: "Confirm & submit" },
  ];

  // Sonner demo functions
  const showSuccessToast = () => {
    toast.success("Campaign created successfully!", {
      description: "Campaign 'Summer Sale 2024' has been added to Cycle 2515",
    });
  };

  const showErrorToast = () => {
    toast.error("Failed to upload evidence", {
      description: "Network error occurred. Please try again.",
    });
  };

  const showWarningToast = () => {
    toast.warning("Pack is incomplete", {
      description: "3 stores are missing format assignments",
    });
  };

  const showInfoToast = () => {
    toast.info("Operator submitted task", {
      description: "John Murphy completed 15 tasks at Tesco Dublin Central",
    });
  };

  const showLoadingToast = () => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 3000)),
      {
        loading: "Generating PDF sign-off sheet...",
        success: "PDF generated successfully!",
        error: "Failed to generate PDF",
      }
    );
  };

  const showMultipleToasts = () => {
    toast.success("Task 1 completed");
    setTimeout(() => toast.success("Task 2 completed"), 500);
    setTimeout(() => toast.success("Task 3 completed"), 1000);
    setTimeout(() => toast.info("All tasks completed!"), 1500);
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-8 h-8 text-blue-600" />
          <h1 className="text-4xl font-bold">Component Showcase</h1>
        </div>
        <p className="text-muted-foreground text-lg">
          Interactive demos of recommended shadcn/ui components for ReActor
        </p>
        <Badge variant="outline" className="mt-2">
          Try each component and see how it would work in your app
        </Badge>
      </div>

      <Separator />

      {/* Sonner Toast Demos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">1️⃣</span> Sonner - Modern Toast Notifications
          </CardTitle>
          <CardDescription>
            Better than the current toast component - stacks elegantly, promise support, smoother animations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Button onClick={showSuccessToast} variant="default">
              ✅ Success Toast
            </Button>
            <Button onClick={showErrorToast} variant="destructive">
              ❌ Error Toast
            </Button>
            <Button onClick={showWarningToast} variant="outline" className="border-amber-500 text-amber-600">
              ⚠️ Warning Toast
            </Button>
            <Button onClick={showInfoToast} variant="secondary">
              ℹ️ Info Toast
            </Button>
            <Button onClick={showLoadingToast} variant="outline">
              ⏳ Loading Promise
            </Button>
            <Button onClick={showMultipleToasts} variant="outline">
              📚 Multiple Toasts
            </Button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-blue-900 font-medium mb-2">✨ Why Sonner is better:</p>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Stacks multiple notifications without overlap</li>
              <li>Promise-based API for async operations (perfect for PDF generation, uploads)</li>
              <li>Swipe to dismiss on mobile</li>
              <li>Auto-dismisses after timeout</li>
              <li>Much smoother animations</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Combobox Demos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">2️⃣</span> Combobox - Searchable Select
          </CardTitle>
          <CardDescription>
            Perfect for long lists - search through stores, operators, retailers instead of scrolling
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Store Selector */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Store className="w-4 h-4 text-muted-foreground" />
              <label className="text-sm font-medium">Select Store</label>
            </div>
            <Combobox
              options={stores}
              value={selectedStore}
              onValueChange={setSelectedStore}
              placeholder="Search stores..."
              searchPlaceholder="Type to search stores..."
              emptyText="No store found."
            />
            {selectedStore && (
              <p className="text-sm text-muted-foreground">
                Selected: <span className="font-medium text-foreground">
                  {stores.find(s => s.value === selectedStore)?.label}
                </span>
              </p>
            )}
          </div>

          <Separator />

          {/* Operator Selector */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <label className="text-sm font-medium">Assign Operator</label>
            </div>
            <Combobox
              options={operators}
              value={selectedOperator}
              onValueChange={setSelectedOperator}
              placeholder="Search operators..."
              searchPlaceholder="Type to search operators..."
              emptyText="No operator found."
            />
            {selectedOperator && (
              <p className="text-sm text-muted-foreground">
                Assigned: <span className="font-medium text-foreground">
                  {operators.find(o => o.value === selectedOperator)?.label}
                </span>
              </p>
            )}
          </div>

          <Separator />

          {/* Retailer Selector */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Store className="w-4 h-4 text-muted-foreground" />
              <label className="text-sm font-medium">Select Retailer</label>
            </div>
            <Combobox
              options={retailers}
              value={selectedRetailer}
              onValueChange={setSelectedRetailer}
              placeholder="Search retailers..."
              searchPlaceholder="Type to search retailers..."
              emptyText="No retailer found."
            />
            {selectedRetailer && (
              <p className="text-sm text-muted-foreground">
                Selected: <span className="font-medium text-foreground">
                  {retailers.find(r => r.value === selectedRetailer)?.label}
                </span>
              </p>
            )}
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-green-900 font-medium mb-2">✨ Use cases in ReActor:</p>
            <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
              <li>Store selector in pack wizard (search through 100+ stores)</li>
              <li>Operator assignment (find operators by name quickly)</li>
              <li>Retailer/Advertiser selection in campaign creation</li>
              <li>Format selection when you have many formats</li>
              <li>Any dropdown with 10+ options becomes easier to use</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Date Range Picker Demo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">3️⃣</span> Date Range Picker
          </CardTitle>
          <CardDescription>
            Select campaign date ranges, filter by cycle dates, operator schedules
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <label className="text-sm font-medium">Campaign Duration</label>
            </div>
            <DateRangePicker
              dateRange={campaignDateRange}
              onDateRangeChange={setCampaignDateRange}
              placeholder="Select campaign start and end dates"
            />
            {campaignDateRange?.from && campaignDateRange?.to && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-2">
                <p className="text-sm font-medium mb-1">Selected Range:</p>
                <p className="text-sm text-muted-foreground">
                  {campaignDateRange.from.toLocaleDateString()} → {campaignDateRange.to.toLocaleDateString()}
                  <span className="ml-2 font-medium">
                    ({Math.ceil((campaignDateRange.to.getTime() - campaignDateRange.from.getTime()) / (1000 * 60 * 60 * 24))} days)
                  </span>
                </p>
              </div>
            )}
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-purple-900 font-medium mb-2">✨ Use cases in ReActor:</p>
            <ul className="text-sm text-purple-800 space-y-1 list-disc list-inside">
              <li>Campaign creation - set start and end dates</li>
              <li>Cycle date selection</li>
              <li>Filter campaigns by date range</li>
              <li>Operator availability scheduling</li>
              <li>Reports - select date range for analytics</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Multi-Select Demo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">4️⃣</span> Multi-Select
          </CardTitle>
          <CardDescription>
            Select multiple items at once - perfect for pack creation and bulk operations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Stores Multi-Select */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Store className="w-4 h-4 text-muted-foreground" />
              <label className="text-sm font-medium">Select Stores for Pack</label>
            </div>
            <MultiSelect
              options={stores}
              selected={selectedStores}
              onChange={setSelectedStores}
              placeholder="Select multiple stores..."
            />
            {selectedStores.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Selected {selectedStores.length} store{selectedStores.length === 1 ? '' : 's'}
              </p>
            )}
          </div>

          <Separator />

          {/* Formats Multi-Select */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <PackagePlus className="w-4 h-4 text-muted-foreground" />
              <label className="text-sm font-medium">Select Media Formats</label>
            </div>
            <MultiSelect
              options={formats}
              selected={selectedFormats}
              onChange={setSelectedFormats}
              placeholder="Select multiple formats..."
            />
            {selectedFormats.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Selected {selectedFormats.length} format{selectedFormats.length === 1 ? '' : 's'}
              </p>
            )}
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-orange-900 font-medium mb-2">✨ Use cases in ReActor:</p>
            <ul className="text-sm text-orange-800 space-y-1 list-disc list-inside">
              <li>Pack wizard - select multiple stores at once</li>
              <li>Campaign creation - choose multiple formats</li>
              <li>Bulk operations - select stores to update</li>
              <li>Operator assignment - pick multiple campaigns</li>
              <li>Much faster than selecting one-by-one!</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Stepper Demo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-2xl">5️⃣</span> Stepper (Progress Indicator)
          </CardTitle>
          <CardDescription>
            Show progress through multi-step workflows like campaign creation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <Stepper steps={campaignSteps} currentStep={currentStep} />

            <div className="flex justify-center gap-4 mt-6">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
              >
                Previous
              </Button>
              <Button
                onClick={() => setCurrentStep(Math.min(campaignSteps.length - 1, currentStep + 1))}
                disabled={currentStep === campaignSteps.length - 1}
              >
                Next
              </Button>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
              <p className="text-sm font-medium mb-2">Current Step: {campaignSteps[currentStep].title}</p>
              <p className="text-sm text-muted-foreground">{campaignSteps[currentStep].description}</p>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-indigo-900 font-medium mb-2">✨ Use cases in ReActor:</p>
            <ul className="text-sm text-indigo-800 space-y-1 list-disc list-inside">
              <li>Campaign creation wizard - show progress through steps</li>
              <li>Pack creation - guide users through the flow</li>
              <li>Operator onboarding - multi-step setup process</li>
              <li>Form wizards - break complex forms into steps</li>
              <li>Better UX - users know where they are in the process</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardHeader>
          <CardTitle>🎯 Next Steps</CardTitle>
          <CardDescription>
            After trying these components, decide which ones to adopt
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold mb-2">Sonner</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Replace current toast across entire app
              </p>
              <Badge variant="secondary">30 min effort</Badge>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold mb-2">Combobox</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Use in pack wizard, campaign creation
              </p>
              <Badge variant="secondary">1-2 hours</Badge>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold mb-2">Date Range Picker</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Use in campaign forms, filters
              </p>
              <Badge variant="secondary">1 hour</Badge>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold mb-2">Multi-Select</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Bulk operations, pack creation
              </p>
              <Badge variant="secondary">1 hour</Badge>
            </div>
            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold mb-2">Stepper</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Wizards, multi-step processes
              </p>
              <Badge variant="secondary">30 min</Badge>
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <Button onClick={() => window.location.href = "/campaigns-v3"} variant="default">
              View TanStack Table POC
            </Button>
            <Button onClick={() => window.location.href = "/campaigns"} variant="outline">
              Back to Original Campaigns
            </Button>
          </div>
        </CardContent>
      </Card>

      <Toaster />
    </div>
  );
}
